import {Component, NgZone} from "@angular/core";

import {ModalConfig} from "../services/notification/modal.config";
import {EventBus} from "../event/EventBus";
import "rxjs/add/operator/map";
import "rxjs/add/operator/subscribeOn";
import {ComponentBase} from "../base/ComponentBase";
import {BugsnagLoggingService} from "../services/logging/bugsnag-logging.service";
import {PipelineEvent, PIPELINE_STATUS} from "../event/PipelineEvent";

import {RaVersion} from "../services/configuration/ra.version";
import {PipelineService} from "../services/pipeline/pipeline.service";

import {IDictionaryEntry} from  "../api/data-model";
import {HttpClient} from "@angular/common/http";
import {filter} from "rxjs/operators";
import {IExpansions} from "../services/pipeline/batch.pipeline.service";

interface IExportSelection {
    [index: string]: boolean;
}

@Component({
    selector: "pipeline-pronunciations-modal",
    templateUrl: "./pipeline.pronunciations.modal.component.html",
    styleUrls: ["./pipeline.component.css"]
})
export class PipelinePronunciationsModalComponent extends ComponentBase {

    public activateModal: boolean = false;
    public status = "";
    public currentWord: string;
    public currentValues: string[];
    public currentIndex: number;
    public nearMatches: IDictionaryEntry[];
    public enterAsWords = false;
    private numNearMatches = 5;

    constructor (
        private http:HttpClient,
        private eventBus: EventBus,
        private modalConfig: ModalConfig,
        private logService: BugsnagLoggingService,
        private _ngZone: NgZone,
        private pipelineService:PipelineService
    ) {
        super("PipelinePronunciationsModalComponent");
        console.log("constructing PipelinePronunciationsModal");
        this.subscribeObserve();
    }

    private subscribeObserve():void {
        let sub = this.eventBus.pipe(filter((e:PipelineEvent) =>
                                       (e.type === PipelineEvent.TYPE) &&
                                       (e.status === PIPELINE_STATUS.pronunciations)))
            .subscribe((e:PipelineEvent) => {
                console.log("activating pipeline pronunciations modal");
                this.activateModal = true;
                this.currentWord = null;
                this.currentValues = [];
                this.nearMatches = [];
                this.currentIndex = -1;
                this.goNextWord(false);
            });
        this.subscriptions.push(sub);
    }

    public goNextWord(save: boolean): void {
        this.currentValues = this.currentValues.filter(v => v && v !== "");
        if (this.enterAsWords) {
            if (save) {
                this.pipelineService.saveExpansionEntry(this.currentWord, this.currentValues);
                this.pipelineService.removeEntry(this.currentWord);
            } else {
                this.currentIndex++;
            }
        } else {
            if (save) {
                const entry: IDictionaryEntry = {word: this.currentWord, pronunciations: this.currentValues};
                this.pipelineService.saveDictionaryEntry(entry);
                this.pipelineService.removeEntry(this.currentWord);
            } else {
                this.currentIndex++;
            }
        }
        this.enterAsWords = false;
        this.retrieveNextWord();

    }

    private retrieveNextWord(): void {
        if (this.pipelineService.numMissingEntries() > 0) {
            // wrap back to the beginning of the list if we have reached the end.
            if (this.currentIndex >= this.pipelineService.numMissingEntries()) {
                this.currentIndex = 0;
            }
            this.currentWord = this.pipelineService.getMissingWord(this.currentIndex);
            this.currentValues = [""];
            this.getNearMatches(this.currentWord);
        } else {
            this.currentWord = null;
            this.currentIndex = -1;
            this.nearMatches = [];
        }
    }

    public goPreviousWord(): void {
        if (this.currentIndex > 0) {
            this.currentIndex--;
            this.currentWord = this.pipelineService.getMissingWord(this.currentIndex);
            this.currentValues = [""];
            this.getNearMatches(this.currentWord);
        }

    }

    public nextWord(): boolean {
        return !!this.pipelineService.getMissingWord(this.currentIndex + 1);
    }

    public previousWord(): boolean {
        return this.currentIndex > 0;

    }

    private getNearMatches(word: string): void {
        this.nearMatches = this.pipelineService.getNearMatches(word, this.numNearMatches);
    }

    public getPronunciations(entry: IDictionaryEntry): string {
        return entry.word + ": " + entry.pronunciations.join(", ");
    }

    public savedEntries(): string[] {
        return this.currentValues.length > 0 ? this.currentValues.slice(1, this.currentValues.length) : [];
    }

    public addValue(): void {
        this.currentValues.unshift("");
        // trim white space from the entry we have just shifted/saved.
        if (this.currentValues.length > 1) {
            this.currentValues[1] = this.currentValues[1].trim();
        }
    }

    public validValues(): boolean {
        if (!this.currentWord) {
            return true; // finished entering, allow save/export
        }
        let trimmedEntry = JSON.parse(JSON.stringify(this.currentValues));
        if (trimmedEntry.length > 0) {
            trimmedEntry[0] = this.currentValues[0].trim();
        }
        if (!this.enterAsWords) {
            return this.pipelineService.pronunciationsValid({word: this.currentWord, pronunciations: trimmedEntry});
        } else {
            return trimmedEntry.length > 0 && trimmedEntry[0].length > 0 && trimmedEntry[0].search(/[^a-zA-Z' ]/) === -1;
        }
    }

    public entryValid(): boolean {
        if (!this.currentWord) {
            return false;
        }
        let validSavedEntry = (this.savedEntries().length > 0) && (this.currentValues[0] === "");
        return this.validValues() || validSavedEntry;
    }

    public exportAll(): void {
        this.goNextWord(true);
        this.pipelineService.exportExpansions();
        this.pipelineService.exportDictionary();
        this.closeModal();
    }

    public saveAll(): void {
        this.goNextWord(true);
        this.pipelineService.saveExpansions();
        this.pipelineService.saveDictionary();
        this.pipelineService.saveExpandedSelections();
        this.closeModal();
    }

    public closeModal(): void {
        this.activateModal = false;
    }

    public numMissingEntries():number {
      return this.pipelineService.numMissingEntries();
    }

}
